import { Controller } from "@hotwired/stimulus"

// 一覧の共通フィルタバー（app/views/ca/shared/_filter_bar.html.erb）の挙動。
//
// - 「詳細条件」の開閉。適用中の詳細条件があれば最初から開いた状態にし、件数バッジを出す。
// - 適用中の条件をチップとして描画し、チップの×で1条件だけ解除して再検索する。
//
// チップのラベルは各入力欄の現在値（select なら選択中の option のテキスト）から作るため、
// 一覧ごとに条件名を登録しなくても全画面で同じように動く。
export default class extends Controller {
  static targets = ["advanced", "advancedToggle", "advancedCount", "advancedCaret", "chips"]

  connect() {
    this.renderChips()
    // 詳細条件が適用されている状態で折りたたまれていると、何で絞られているか分からないため開いておく。
    if (this.hasAdvancedTarget) {
      const count = this.activeControls(this.advancedTarget).length
      this.updateAdvancedCount(count)
      this.setAdvancedExpanded(count > 0)
    }
  }

  toggleAdvanced() {
    this.setAdvancedExpanded(this.advancedTarget.classList.contains("hidden"))
  }

  setAdvancedExpanded(expanded) {
    this.advancedTarget.classList.toggle("hidden", !expanded)
    this.advancedTarget.classList.toggle("flex", expanded)
    this.advancedToggleTarget.setAttribute("aria-expanded", String(expanded))
    if (this.hasAdvancedCaretTarget) this.advancedCaretTarget.textContent = expanded ? "▲" : "▼"
  }

  updateAdvancedCount(count) {
    if (!this.hasAdvancedCountTarget) return

    this.advancedCountTarget.textContent = count
    this.advancedCountTarget.classList.toggle("hidden", count === 0)
  }

  // 値が入っている（＝絞り込みに効いている）入力欄を集める。
  // hidden は画面外の受け渡し用（集計基準など）、disabled は送信されないので条件として扱わない。
  // 空白だけの検索語もサーバ側で strip されて条件にならないため除く。
  activeControls(root) {
    const controls = root.querySelectorAll("select, input[type='text'], input[type='search'], input[type='checkbox']")
    return Array.from(controls).filter((control) => {
      if (control.disabled) return false
      if (control.type === "checkbox") return control.checked
      return control.value.trim() !== ""
    })
  }

  // 適用中の条件をチップとして描き直す。
  renderChips() {
    // チップ表示を持たない一覧に接続されることもあるため、ターゲット未定義なら何もしない。
    if (!this.hasChipsTarget) return

    const chips = this.activeControls(this.element)
      .filter((control) => !this.selfRendering(control))
      .map((control) => this.buildChip(control))
    this.chipsTarget.replaceChildren(...chips)
  }

  // 複数選択（tag-select）は選択済みを自前のタグで見せて個別に解除もできるため、
  // チップに出すと同じ条件が二重に並ぶ。チップ側は描かない。
  selfRendering(control) {
    return control.tagName === "SELECT" && control.multiple
  }

  buildChip(control) {
    const chip = document.createElement("span")
    chip.className =
      "inline-flex items-center gap-1 rounded-full bg-indigo-50 px-2.5 py-1 text-xs text-indigo-700 ring-1 ring-indigo-100"

    const label = document.createElement("span")
    label.textContent = this.chipLabel(control)
    chip.appendChild(label)

    const remove = document.createElement("button")
    remove.type = "button"
    remove.className = "cursor-pointer leading-none text-indigo-400 hover:text-indigo-700"
    remove.setAttribute("aria-label", `${label.textContent} の条件を解除`)
    remove.textContent = "×"
    remove.addEventListener("click", () => this.clearControl(control))
    chip.appendChild(remove)

    return chip
  }

  // チップの文言。select は選択中の option、チェックボックスは対応するラベル、検索語は「「営業」を含む」。
  // ラベルが引けないときはパラメータ名が出てしまうため、囲みラベルと label[for] の両方を見る。
  chipLabel(control) {
    if (control.tagName === "SELECT") return control.selectedOptions[0]?.text?.trim() || control.value
    if (control.type === "checkbox") return this.checkBoxLabel(control)?.trim() || control.name

    return `「${control.value.trim()}」を含む`
  }

  checkBoxLabel(control) {
    const label = control.closest("label") ||
      (control.id && this.element.querySelector(`label[for="${control.id}"]`))
    return label?.textContent
  }

  // 1条件だけ解除して再検索する。
  clearControl(control) {
    if (control.type === "checkbox") {
      control.checked = false
    } else {
      control.value = ""
    }
    this.element.requestSubmit()
  }
}
